import { Controller } from "@hotwired/stimulus"

// Command palette ⌘K — quick navigation + live search (accounts/contacts/deals).
// Filtre client-side sur la nav statique + fetch JSON /search pour la recherche live.
export default class extends Controller {
  static targets = ["backdrop", "panel", "input", "list", "item", "empty", "results", "loading"]
  static values = { searchUrl: { type: String, default: "/search" } }

  connect() {
    this.activeIndex = 0
    this.searchDebounce = null
    this.lastQuery = ""
    if (this.hasBackdropTarget) this.backdropTarget.dataset.open = "false"
  }

  disconnect() {
    if (this.searchDebounce) clearTimeout(this.searchDebounce)
  }

  open(event) {
    if (event) event.preventDefault()
    if (!this.hasBackdropTarget) return
    this.backdropTarget.dataset.open = "true"
    this.activeIndex = 0
    this.#updateActive()
    requestAnimationFrame(() => {
      this.inputTarget?.focus()
      this.inputTarget?.select()
    })
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.hasBackdropTarget) return
    if (this.backdropTarget.dataset.open !== "true") return
    this.backdropTarget.dataset.open = "false"
    if (this.hasInputTarget) this.inputTarget.value = ""
    this.#clearResults()
    this.#resetFilter()
  }

  closeIfBackdrop(event) {
    if (event.target === this.backdropTarget) this.close(event)
  }

  filter() {
    const query = (this.inputTarget.value || "").trim()
    const lower = query.toLowerCase()

    // 1. Filtre client-side nav statique
    let visible = 0
    this.itemTargets.forEach((item) => {
      const label = (item.dataset.label || item.innerText || "").toLowerCase()
      const match = lower === "" || label.includes(lower)
      item.hidden = !match
      if (match) visible++
    })
    this.listTarget.querySelectorAll(".cp-group-label").forEach((label) => {
      let next = label.nextElementSibling
      let hasVisible = false
      while (next && !next.classList.contains("cp-group-label")) {
        if (next.classList.contains("cp-item") && !next.hidden) { hasVisible = true; break }
        next = next.nextElementSibling
      }
      label.hidden = !hasVisible
    })

    // 2. Live search côté serveur pour ≥ 2 caractères
    if (query.length >= 2) {
      this.#scheduleLiveSearch(query)
    } else {
      this.#clearResults()
    }

    if (this.hasEmptyTarget) this.emptyTarget.hidden = visible > 0 || query.length >= 2
    this.activeIndex = 0
    this.#updateActive()
  }

  navigate(event) {
    const visibleItems = this.#visibleItems()
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, visibleItems.length - 1)
      this.#updateActive()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this.#updateActive()
    } else if (event.key === "Enter") {
      event.preventDefault()
      const active = visibleItems[this.activeIndex]
      if (active) active.click()
    }
  }

  #visibleItems() {
    const live = this.hasResultsTarget ? Array.from(this.resultsTarget.querySelectorAll(".cp-item")) : []
    const nav = this.itemTargets.filter((i) => !i.hidden)
    return [...live, ...nav]
  }

  #scheduleLiveSearch(query) {
    if (this.searchDebounce) clearTimeout(this.searchDebounce)
    this.searchDebounce = setTimeout(() => this.#runLiveSearch(query), 180)
  }

  async #runLiveSearch(query) {
    if (query === this.lastQuery) return
    this.lastQuery = query
    if (this.hasLoadingTarget) this.loadingTarget.hidden = false

    try {
      const sep = this.searchUrlValue.includes("?") ? "&" : "?"
      const url = `${this.searchUrlValue}${sep}q=${encodeURIComponent(query)}`
      const res = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!res.ok) throw new Error(`search ${res.status}`)
      const data = await res.json()
      if (this.inputTarget.value.trim() !== query) return
      this.#renderResults(data)
    } catch (err) {
      console.error("[command-palette] search failed", err)
    } finally {
      if (this.hasLoadingTarget) this.loadingTarget.hidden = true
    }
  }

  #renderResults(results) {
    if (!this.hasResultsTarget) return
    if (!results || results.length === 0) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.hidden = true
      return
    }

    const groups = {}
    results.forEach((r) => {
      (groups[r.type_label] ||= []).push(r)
    })

    let html = ""
    Object.entries(groups).forEach(([label, items]) => {
      html += `<div class="cp-group-label">${this.#escape(label)}</div>`
      items.forEach((r) => {
        const subtitle = r.subtitle ? `<span class="cp-item-hint">${this.#escape(r.subtitle)}</span>` : ""
        html += `<a href="${r.url}" class="cp-item cp-item-live">
          <span class="cp-item-icon">${this.#iconSvg(r.icon)}</span>
          <span class="cp-item-label">${this.#escape(r.label)}</span>
          ${subtitle}
        </a>`
      })
    })

    this.resultsTarget.innerHTML = html
    this.resultsTarget.hidden = false
    this.activeIndex = 0
    this.#updateActive()
  }

  #clearResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.hidden = true
    }
    this.lastQuery = ""
  }

  #updateActive() {
    const visibleItems = this.#visibleItems()
    visibleItems.forEach((i) => (i.dataset.active = "false"))
    this.itemTargets.forEach((i) => (i.dataset.active = "false"))
    const active = visibleItems[this.activeIndex]
    if (active) {
      active.dataset.active = "true"
      active.scrollIntoView({ block: "nearest" })
    }
  }

  #resetFilter() {
    this.itemTargets.forEach((i) => { i.hidden = false })
    this.listTarget.querySelectorAll(".cp-group-label").forEach((l) => { l.hidden = false })
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    this.activeIndex = 0
  }

  #escape(str) {
    const div = document.createElement("div")
    div.textContent = String(str ?? "")
    return div.innerHTML
  }

  #iconSvg(name) {
    const paths = {
      "building-office": '<path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />',
      "user": '<path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />',
      "currency-euro": '<path stroke-linecap="round" stroke-linejoin="round" d="M14.25 7.756a4.5 4.5 0 1 0 0 8.488M7.5 10.5h5.25m-5.25 3h5.25M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />'
    }
    const body = paths[name] || paths["user"]
    return `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">${body}</svg>`
  }
}
